import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';

interface IProducaoAgroecologicaData {
  praticaAgroecologica: string;
  certificacoes: string[];
  outrasCertificacoes: string[];
  produtos?: any[];
}

// Mapeamento das certificações para nomes legíveis
const certificationNameMap: Record<string, string> = {
  'CERTIFICAÇÃO POR AUDITORIA (OCS)': 'Certificação por auditoria (OCS)',
  'SISTEMA PARTICIPATIVO DE GARANTIA (SPG)': 'Sistema Participativo de Garantia (SPG)',
  'CONTROLE SOCIAL EM VENDA DIRETA': 'Controle Social na venda direta',
  'OUTRO': 'Outras certificações'
};

export const ProducaoAgroecologicaVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  const dadosParciais = useCuffaStore(state => state.dadosParciais);

  const [dados, setDados] = useState<IProducaoAgroecologicaData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }

      try {
        const response = await CuffaAPI.getDataByID(uuid);

        const apiData = response && response.dados ? response.dados : null;

        const mappedData: IProducaoAgroecologicaData | null = apiData ? {
          praticaAgroecologica: apiData.family_practices_agroecological_organic ? 'sim' : 'não',
          certificacoes: apiData.family_certification_types?.types || [],
          outrasCertificacoes:
            apiData.family_certification_types?.other_certifications?.map(
              (cert: any) => cert.description || cert
            ) || [],
          produtos: apiData.conventional_products || apiData.products || []
        } : null;

        const local = dadosParciais?.producaoAgroecologica;
        if (local) {

          const praticaValue = typeof local.pratica === 'boolean'
            ? local.pratica
            : typeof local.practices_agroecological_organic === 'boolean'
              ? local.practices_agroecological_organic
              : null;

          const certs: string[] = Array.isArray(local.certificacoes)
            ? local.certificacoes
            : Array.isArray(local.certification_types)
              ? local.certification_types
              : (local.certification_types?.types || local.family_certification_types?.types) || [];

          const otherCerts: string[] = Array.isArray(local.otherCertifications)
            ? local.otherCertifications
            : Array.isArray(local.other_certifications)
              ? local.other_certifications
              : (local.other_certifications || local.otherCertifications || []);

          const produtosLocais = Array.isArray(local.conventional_products)
            ? local.conventional_products
            : Array.isArray(local.products)
              ? local.products
              : (local.conventional_products || local.products || []);

          const localMapped: IProducaoAgroecologicaData = {
            praticaAgroecologica: praticaValue ? 'sim' : 'não',
            certificacoes: certs,
            outrasCertificacoes: otherCerts.map((c: any) => c.description || c),
            produtos: produtosLocais
          };

          setDados(localMapped);
        } else if (mappedData) {
          setDados(mappedData);
        } else {
          console.error("Dados de produção agroecológica não encontrados na resposta da API:", response);
          setDados(null);
        }
      } catch (error) {
        console.error("Erro ao buscar dados de produção agroecológica:", error);
        setDados(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uuid]);

  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produção Agroecológica" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produção Agroecológica" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Typography>Não foi possível carregar os dados. Verifique o console para mais detalhes.</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização da Produção Agroecológica" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Produção Agroecológica ou Orgânica" width="100%">
            {/* Seção: Prática agroecológica */}
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                A unidade familiar pratica produção agroecológica ou orgânica?
              </Typography>
              <Typography variant="body1" gutterBottom>
                {dados.praticaAgroecologica === 'sim' ? 'Sim' : 'Não'}
              </Typography>
            </Paper>

            {/* Seção: Certificações (se aplicável) */}
            {dados.praticaAgroecologica === 'sim' && (
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Certificações da produção
                </Typography>

                {/* Certificações padrão */}
                {dados.certificacoes.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="subtitle1" gutterBottom>
                      Tipos de certificação:
                    </Typography>
                    <Grid container spacing={1}>
                      {dados.certificacoes
                        .filter(cert => cert !== 'OUTRO')
                        .map((cert, index) => (
                          <Grid item key={index}>
                            <Chip
                              label={certificationNameMap[cert] || cert}
                              color="primary"
                              variant="outlined"
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                )}

                {/* Outras certificações */}
                {dados.outrasCertificacoes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Outras certificações:
                    </Typography>
                    <List dense>
                      {dados.outrasCertificacoes.map((certificacao, index) => (
                        <div key={index}>
                          <ListItem>
                            <ListItemText primary={certificacao} />
                          </ListItem>
                          {index < dados.outrasCertificacoes.length - 1 && <Divider />}
                        </div>
                      ))}
                    </List>
                  </Box>
                )}

                {dados.certificacoes.length === 0 && dados.outrasCertificacoes.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma certificação registrada
                  </Typography>
                )}
              </Paper>
            )}

            {dados.produtos && dados.produtos.length > 0 && (
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Produtos relacionados
                </Typography>
                <List>
                  {dados.produtos.map((p, idx) => {
                    const fieldLabels: Record<string, string> = {
                      uuid: 'Identificador',
                      name: 'Nome do Produto',
                      custom_product_name: 'Nome Personalizado',
                      production_area_hectares: 'Área de produção (hectares)',
                      volume_produced: 'Volume produzido',
                      volume_produced_unit: 'Unidade do volume produzido',
                      volume_commercialized: 'Volume comercializado',
                      volume_commercialized_unit: 'Unidade do volume comercializado',
                      average_commercialized_value: 'Valor médio comercializado (R$)',
                      volume_self_consumption: 'Volume autoconsumido',
                      volume_self_consumption_unit: 'Unidade do volume autoconsumido',
                      volume_lost: 'Volume perdido',
                      volume_lost_unit: 'Unidade do volume perdido',
                      is_processed: 'Processado',
                      production_type: 'Tipo de produção',
                      product_type: 'Tipo de produto',
                      availability_months: 'Meses de disponibilidade',
                    };
                    const entries = Object.entries(p);
                    return (
                      <div key={idx}>
                        <ListItem alignItems="flex-start">
                          <ListItemText
                            primary={
                              <Typography fontWeight="bold">
                                {p.custom_product_name || p.name || p.nome || p.product_id || p.uuid || `Produto ${idx + 1}`}
                              </Typography>
                            }
                            secondary={
                              <Box component="dl" sx={{ m: 0 }}>
                                {entries.map(([key, value]) => {
                                  if (key === 'custom_product_name' || key === 'name' || key === 'nome' || key === 'product_id') return null;
                                  const label = fieldLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                  let displayValue: string | JSX.Element = '';
                                  if (typeof value === 'object' && value !== null) {
                                    if (key === 'availability_months') {
                                      const meses = Object.entries(value)
                                        .filter(([_, v]) => v)
                                        .map(([mes]) => {
                                          const mesesPt: Record<string, string> = {
                                            available_year_round: 'Ano todo',
                                            january: 'Jan',
                                            february: 'Fev',
                                            march: 'Mar',
                                            april: 'Abr',
                                            may: 'Mai',
                                            june: 'Jun',
                                            july: 'Jul',
                                            august: 'Ago',
                                            september: 'Set',
                                            october: 'Out',
                                            november: 'Nov',
                                            december: 'Dez',
                                          };
                                          return mesesPt[mes] || mes;
                                        });
                                      displayValue = meses.length > 0 ? meses.join(', ') : 'Não informado';
                                    } else {
                                      displayValue = JSON.stringify(value, null, 2);
                                    }
                                  } else if (typeof value === 'boolean') {
                                    displayValue = value ? 'Sim' : 'Não';
                                  } else if (value !== null && value !== undefined) {
                                    displayValue = value.toString();
                                  } else {
                                    displayValue = 'Não informado';
                                  }
                                  return (
                                    <Box key={key} component="div" sx={{ mb: 1 }}>
                                      <Typography component="dt" variant="caption" color="text.secondary" sx={{ display: 'inline', fontWeight: 600 }}>
                                        {label}:
                                      </Typography>{' '}
                                      <Typography component="dd" variant="body2" sx={{ display: 'inline' }}>
                                        {displayValue}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            }
                          />
                        </ListItem>
                        {(idx < ((dados.produtos?.length ?? 0) - 1)) && (<Divider />)}
                      </div>
                    );
                  })}
                </List>
              </Paper>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.CADASTRAR_PRODUTOS_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (uuid) {
                    navigate(ROUTES.DETALHES_PRODUCAO_VISUALIZAR.replace(':uuid', uuid));
                  }
                }}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

export default ProducaoAgroecologicaVisualizar;
